import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/auth/auth.service';
import { ChatRequest, ChatStreamEvent, StreamFinalData } from '../../../core/models/api.models';

// Client safety bound, including authentication. This does not replace server cancellation.
export const CHAT_INTERACTION_TIMEOUT_MS = new InjectionToken<number>('CHAT_INTERACTION_TIMEOUT_MS', {
  providedIn: 'root', factory: () => 5 * 60_000,
});

@Injectable({ providedIn: 'root' })
export class ChatStreamService {
  private readonly auth = inject(AuthService);
  private readonly timeoutMs = inject(CHAT_INTERACTION_TIMEOUT_MS);

  stream(request: ChatRequest): Observable<ChatStreamEvent> {
    return new Observable(observer => {
      const controller = new AbortController();
      const timeout = setTimeout(() => {
        observer.error(new Error('Chat interaction timed out.'));
        controller.abort();
      }, this.timeoutMs);
      void this.consume(request, controller.signal, event => observer.next(event))
        .then(() => observer.complete())
        .catch(error => observer.error(error));
      return () => { clearTimeout(timeout); controller.abort(); };
    });
  }

  private async consume(request: ChatRequest, signal: AbortSignal, emit: (event: ChatStreamEvent) => void): Promise<void> {
    const token = await this.auth.acquireAccessToken();
    signal.throwIfAborted();
    const response = await fetch(`${environment.apiUrl}/chats/interaction`, {
      method: 'POST', signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(`Falha no streaming (${response.status}).`);
    if (!response.body) throw new Error('O servidor não retornou um stream de resposta.');

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    const processLine = (line: string): boolean => {
      const event = parseChatStreamLine(line);
      if (event?.type === 'error') throw new Error('Chat processing failed.');
      if (event) emit(event);
      return event?.type === 'final';
    };
    try {
      while (true) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop() ?? '';
        for (const line of lines) if (processLine(line)) return;
        if (done) break;
      }
      if (buffer.trim() && processLine(buffer)) return;
      throw new Error('Chat stream ended without a final response.');
    } finally {
      // Do not wait for the remote peer to close before completing the observable.
      void reader.cancel().catch(() => undefined);
      reader.releaseLock();
    }
  }
}

export function parseChatStreamLine(line: string): ChatStreamEvent | null {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(':')) return null;
    const raw = trimmed.startsWith('data:') ? trimmed.slice(5).trim() : trimmed;
    if (!raw || raw === '[DONE]') return null;
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { return null; }
    if (!parsed || typeof parsed !== 'object') return null;
    const value = parsed as Record<string, unknown>;
    if (value['type'] === 'error') {
      // Server diagnostics must never be rendered in the chat.
      return { type: 'error' };
    } else if (value['type'] === 'progress') {
      return { type: 'progress', step: String(value['step'] ?? ''), message: String(value['message'] ?? ''), order: Number(value['order'] ?? 0) };
    } else if (value['type'] === 'token') {
      return { type: 'token', message: String(value['message'] ?? '') };
    } else if (isLegacyFinal(value['data'])) {
      return { type: 'final', data: value['data'] as StreamFinalData };
    }
    return null;
}

function isLegacyFinal(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const data = value as Record<string, unknown>;
  return typeof data['FolderId'] === 'string' && Boolean(data['FolderId'])
    && isStreamMessage(data['Asc']) && isStreamMessage(data['Message']);
}

function isStreamMessage(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const message = value as Record<string, unknown>;
  return typeof message['Id'] === 'string' && Boolean(message['Id']) && typeof message['Text'] === 'string'
    && (message['MessageFiles'] == null || (Array.isArray(message['MessageFiles']) && message['MessageFiles'].every(file => file && typeof file === 'object')));
}
